<template>
  <v-card>
    <v-card-title>{{ $t('simulacion.modulo.eolica.perfil') }}</v-card-title>
    <v-divider></v-divider>
    <v-card-text>
      <v-row>
        <v-col cols="8" class="d-flex justify-end mb-2"></v-col>

        <v-col cols="4" class="d-flex justify-end mb-2">
          <v-btn color="primary" class="mx-2" @click="exportToCSV">
            {{ $t('comun.boton_exportar') }}
          </v-btn>

          <v-btn :disabled="disabled" color="primary" class="mx-2" @click="importFromCSV">
            {{ $t('comun.boton_importar') }}
            <input
              type="file"
              accept=".csv"
              ref="fileInput"
              style="display: none;"
              @change="handleCSVUpload"
            />
          </v-btn>
        </v-col>

        <v-col cols="12">
          <v-container>
            <v-row>
              <v-col cols="12">
                <div id="app2">
                  <v-grid
                    theme="material"
                    :source="datos"
                    :columns="columns"
                    canFocus
                    range
                    useClipboard
                    :readonly="disabled"
                    filter
                    resize
                    @afteredit="handleCellEdit"
                  ></v-grid>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
      </v-row>

      <v-dialog v-model="showAlert" max-width="400">
        <v-card>
          <v-card-title class="headline">Advertencia</v-card-title>
          <v-card-text>{{ error_msg }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="showAlert = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import VGrid from "@revolist/vue3-datagrid";
import Swal from "sweetalert2";

export default {
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    years: {
      type: Array,
      default: () => [],
    },
    maxRows: {
      type: Number,
      default: 8760,
    },
  },

  components: { VGrid },

  data() {
    return {
      showAlert: false,
      error_msg: "",
      columns: [],
      datos: [],
    };
  },

  watch: {
    modelValue: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.datos = this.ensureTemplate(newVal);
        this.setColumns();
      },
    },

    years: {
  immediate: true,
  deep: true,
  handler() {
    this.datos = this.ensureTemplate(this.datos);
    this.setColumns();
    
  },
},

    "$i18n.locale"() {
      this.setColumns();
    },
  },

  methods: {
    yearKeys() {
      return (this.years || []).map(String);
    },

    normalizeIncoming(arr) {
      const safe = Array.isArray(arr) ? arr : [];
      return safe.slice(0, this.maxRows).map((row, idx) => {
        const periodo = Number(row.periodo ?? row.Hora ?? (idx + 1)) || (idx + 1);
        return { ...row, periodo };
      });
    },

    ensureTemplate(input) {
      const years = this.yearKeys();
      const arr = Array.isArray(input) ? input : [];

      if (!years.length) {
        return this.normalizeIncoming(arr);
      }

      if (!arr.length) {
        return Array.from({ length: this.maxRows }, (_, i) => {
          const row = { periodo: i + 1 };
          years.forEach(y => (row[y] = 0));
          return row;
        });
      }

      const first = arr[0] || {};
      const hasAnnual = first.periodo !== undefined && Object.keys(first).some(k => /^\d{4}$/.test(k));
      const isLegacy = ("perfil" in first) && !hasAnnual;

      if (hasAnnual) {
        return arr.slice(0, this.maxRows).map((r, idx) => {
          const periodo = Number(r.periodo ?? (idx + 1)) || (idx + 1);
          const row = { ...r, periodo };

          years.forEach(y => {
            const raw = row[y];
            const n = typeof raw === "string" ? Number(raw.replace(",", ".")) : Number(raw);
            row[y] = Number.isFinite(n) ? n : 0;
          });

          Object.keys(row).forEach(k => {
            if (k !== "periodo" && /^\d{4}$/.test(k) && !years.includes(k)) delete row[k];
          });

          return row;
        });
      }

      if (isLegacy) {
        return arr.slice(0, this.maxRows).map((r, idx) => {
          const periodo = Number(r.periodo ?? (idx + 1)) || (idx + 1);
          const n = Number(String(r.perfil ?? 0).replace(",", "."));
          const val = Number.isFinite(n) ? n : 0;

          const row = { periodo };
          years.forEach(y => (row[y] = val));
          return row;
        });
      }

      return this.normalizeIncoming(arr);
    },

    setColumns() {
      if (!this.datos.length) {
        this.columns = [];
        return;
      }

      const keys = Object.keys(this.datos[0]);

      this.columns = keys.map(key => ({
        name: key === "periodo" ? this.$t("simulacion.modulo.eolica.hora") : key,
        prop: key,
        size: key === "periodo" ? 90 : 120,
        readonly: key === "periodo",
        cellTemplate: (h, props) => {
          const value = props.model[props.prop];
          return h(
            "span",
            {},
            typeof value === "number"
              ? value.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 20 })
              : (value === 0 ? "0" : (value ?? ""))
          );
        }
      }));

      const idx = this.columns.findIndex(c => c.prop === "periodo");
      if (idx !== -1) {
        const col = this.columns.splice(idx, 1)[0];
        this.columns.unshift(col);
      }
    },

    handleCellEdit(event) {
      const { detail } = event;
      if (!detail?.prop || detail.prop === "periodo") return;

      let inputValue = detail.val;
      if (typeof inputValue === "string") inputValue = inputValue.replace(",", ".");

      const newValue = Number(inputValue);
      const finalValue = isNaN(newValue) ? 0 : newValue;

      const rowIndex = detail.rowIndex;
      this.datos[rowIndex][detail.prop] = finalValue;

      this.$emit("update:modelValue", [...this.datos]);
      this.setColumns();
    },

    exportToCSV() {
      if (!this.columns.length) return;

      const headers = this.columns.map(col => col.prop).join(";");
      const rows = this.datos.map(row =>
        this.columns.map(col => {
          const value = row[col.prop];
          if (typeof value === "number") {
            return value.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 20 });
          }
          return value !== undefined ? value : "";
        }).join(";")
      );

      const csvContent = [headers, ...rows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "perfil-eolica.csv";
      a.click();
      URL.revokeObjectURL(url);
    },

    importFromCSV() {
      this.$refs.fileInput.click();
    },

    handleCSVUpload(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const csvData = e.target.result;

        const [headerLine, ...lines] = csvData
          .split("\n")
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (!headerLine) return;

        if (lines.length > this.maxRows) {
          Swal.fire({
            icon: "error",
            title: this.$t("mensajes.excel_demasiadas_filas_titulo") || "Demasiadas filas",
            html:
              (this.$t("mensajes.excel_demasiadas_filas_texto") || "El fichero supera el máximo permitido.") +
              `<br><b>${lines.length.toLocaleString()}</b> filas detectadas. Máximo permitido: <b>${this.maxRows.toLocaleString()}</b>.`,
            confirmButtonText: "OK",
          });
          event.target.value = "";
          return;
        }

        const headers = headerLine.split(";");
        if (!headers.includes("periodo")) {
          Swal.fire({
            icon: "error",
            title: "CSV inválido",
            text: "Falta la columna 'periodo' en la cabecera.",
          });
          event.target.value = "";
          return;
        }

        const importedData = lines.map((line, idx) => {
          const values = line.split(";");
          const obj = {};

          headers.forEach((h, j) => {
            const raw = values[j] ?? "";
            if (h === "periodo") {
              obj.periodo = Number(String(raw).replace(",", ".")) || (idx + 1);
              return;
            }

            const n = Number(String(raw).replace(",", "."));
            obj[h] = raw === "" ? 0 : (isNaN(n) ? 0 : n);
          });

          return obj;
        });

        this.datos = this.ensureTemplate(importedData);
        this.setColumns();
        this.$emit("update:modelValue", [...this.datos]);
      };

      reader.readAsText(file, "UTF-8");
      event.target.value = "";
    },
  },
};
</script>

<style>
#app2 {
  height: 400px;
  width: 100%;
}
revo-grid {
  height: 100%;
  width: 100%;
  font-size: 12px;
}
</style>